// Motion controller.
//
// Three independent pieces, all idempotent so they can be re-run on every route
// change without stacking listeners:
//
//   1. A single shared IntersectionObserver that adds `.is-visible` to anything
//      tagged with a reveal class. Children of a [data-stagger] container also
//      get a `--reveal-i` index so CSS can derive a per-child delay.
//   2. One rAF-throttled scroll listener that publishes read position
//      (`--scroll-progress`), a `data-scrolled` flag for the condensing nav,
//      and transforms for any [data-parallax] element currently on screen.
//   3. A MutationObserver that re-scans when new nodes mount mid-route, so
//      content revealed by a tab or toggle is never left stranded at opacity 0.
//
// Usage: add `reveal` (or `reveal-left`, `reveal-scale`, …) to an element.
// For a headline wipe use `<span class="reveal-mask"><span>text</span></span>`.

const REVEAL_SELECTOR =
  '.reveal, .reveal-up, .reveal-down, .reveal-left, .reveal-right, ' +
  '.reveal-scale, .reveal-blur, .reveal-fade, .reveal-mask, .rule-draw';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* -------------------------------------------------------------------------
   1. Reveal observer
   ------------------------------------------------------------------------- */

let observer = null;
const watched = new WeakSet();

function ensureObserver() {
  if (observer) return observer;
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return null;
  }
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    {
      root: null,
      // Fire a little before the element is fully on screen so the motion
      // reads as part of the scroll rather than a reaction to it.
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.06,
    }
  );
  return observer;
}

// Index the children of every stagger container so CSS can space their delays.
// data-stagger="0.05" overrides the step for that container.
function indexStaggerGroups(root) {
  root.querySelectorAll('[data-stagger]').forEach((group) => {
    const step = group.getAttribute('data-stagger');
    if (step) group.style.setProperty('--stagger-step', `${step}s`);
    Array.from(group.children).forEach((child, i) => {
      child.style.setProperty('--reveal-i', String(i));
    });
  });
}

export function scanRevealTargets(root = document) {
  if (typeof window === 'undefined') return;

  indexStaggerGroups(root);

  // With reduced motion the CSS already pins everything visible; skip the
  // observer entirely so nothing depends on it.
  if (prefersReducedMotion()) return;

  const obs = ensureObserver();
  if (!obs) return;

  root.querySelectorAll(REVEAL_SELECTOR).forEach((node) => {
    if (watched.has(node) || node.classList.contains('is-visible')) return;
    watched.add(node);

    const rect = node.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      // Already on screen at mount — defer one frame so the "before" state
      // paints first and the transition actually runs.
      requestAnimationFrame(() => node.classList.add('is-visible'));
    } else {
      obs.observe(node);
    }
  });
}

/* -------------------------------------------------------------------------
   2. Scroll controller
   ------------------------------------------------------------------------- */

let scrollBound = false;
let parallaxNodes = [];

function collectParallax(root = document) {
  parallaxNodes = Array.from(root.querySelectorAll('[data-parallax]')).map((el) => ({
    el,
    speed: parseFloat(el.getAttribute('data-parallax')) || 0.15,
  }));
}

function onScroll() {
  const doc = document.documentElement;
  const scrolled = window.scrollY || window.pageYOffset || 0;
  const max = doc.scrollHeight - window.innerHeight;

  doc.style.setProperty('--scroll-progress', max > 0 ? String(scrolled / max) : '0');

  // Drives the nav's condensed state and any other "past the fold" styling.
  if (scrolled > 24) doc.setAttribute('data-scrolled', '');
  else doc.removeAttribute('data-scrolled');

  if (prefersReducedMotion()) return;

  const viewportH = window.innerHeight;
  parallaxNodes.forEach(({ el, speed }) => {
    const rect = el.getBoundingClientRect();
    if (rect.bottom < -200 || rect.top > viewportH + 200) return;
    // Offset from viewport centre, so each element sits at rest when centred.
    const fromCentre = rect.top + rect.height / 2 - viewportH / 2;
    el.style.transform = `translate3d(0, ${(-fromCentre * speed).toFixed(2)}px, 0)`;
  });
}

function bindScroll() {
  if (scrollBound || typeof window === 'undefined') return;
  scrollBound = true;

  let ticking = false;
  const handler = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      onScroll();
      ticking = false;
    });
  };

  window.addEventListener('scroll', handler, { passive: true });
  window.addEventListener('resize', handler, { passive: true });
  onScroll();
}

/* -------------------------------------------------------------------------
   3. Mutation watcher

   Route changes are not the only time reveal targets appear. Tab panels,
   accordions, filtered lists and anything else that mounts after the initial
   render produce fresh nodes the IntersectionObserver has never been told
   about — and because the "before" state is `opacity: 0`, an unobserved node
   is an invisible one. Watching for added nodes keeps that from happening
   again anywhere on the site.
   ------------------------------------------------------------------------- */

let mutationObserver = null;

function bindMutationObserver() {
  if (mutationObserver || typeof MutationObserver === 'undefined') return;
  // Reduced motion pins everything visible in CSS, so there is nothing to do.
  if (prefersReducedMotion()) return;

  let queued = false;
  mutationObserver = new MutationObserver((records) => {
    if (queued) return;
    if (!records.some((record) => record.addedNodes.length > 0)) return;
    queued = true;
    // Coalesce a burst of mounts into one scan on the next frame.
    requestAnimationFrame(() => {
      queued = false;
      scanRevealTargets();
      collectParallax();
    });
  });

  // childList only: scanning writes class names and inline custom properties,
  // and observing attributes as well would feed those writes straight back in.
  mutationObserver.observe(document.body, { childList: true, subtree: true });
}

/* -------------------------------------------------------------------------
   Entry point — safe to call on every route change.
   ------------------------------------------------------------------------- */

export function initScrollReveal() {
  scanRevealTargets();
  collectParallax();
  bindScroll();
  bindMutationObserver();
}
